import React from 'react';

import CoverSheet from '../general/coverSheet';
import DyePenetrantInspection from '../dpt/dyePenetrantInspection';
import ETAnomalyPDF from '../et/ETAnomalyPDF';
import PDFInfoSheet from '../../../themed/pdf/pdfInfoSheet';
import PDFPage from '../../../themed/pdf/pdfPage';
import PDFPhotoPage from '../../../themed/pdf/pdfPhotoPage';
import RTWDigitalRadiography from './rtwDigitalRadiography';
import RTWSummary from './rtwSummary';

import withPDF from '../../../hoc/withPDF';

import * as models from '../../../../utils/models';
import * as utils from '../../../../utils/utilities';

interface RTPReportProps {
	coverSheetData: models.RTWCoverData;
	projectDetails: models.Project;
	isoData: models.PDFImageProps[];
	summarySheetData: models.RTWSummaryOverviewData;
	anomalyData: models.VTEquipmentAnomalyData[];
	lptData: models.DyePenetrantInspectionProps;
	radiographyData: models.RTWRadiographyData;
	radiographySummary: models.RTWRadiographySummaryData;
	photographicRecord: models.PDFImageProps[];
}

const RTWReport = (props: RTPReportProps): JSX.Element => {
	const { documentNumber, pidImages } = props.projectDetails;

	const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: props.coverSheetData?.scopeOfWork,
		},
		{
			label: '2. Visual Examination',
			value: props.coverSheetData?.visualExamination,
		},
		{
			label: '3. Radiographic Examination Procedure',
			value: props.coverSheetData?.radiographProcedure,
		},
		{
			label: '4.  Liquid Penetrant Testing',
			value: props.coverSheetData?.lptProcedure,
		},
		{
			label: '5.  Drawing / P&ID No',
			value: props.coverSheetData?.drawingNo,
		},
		{
			label: '6. Circute No',
			value: props.coverSheetData?.circuteNo,
		},
	];

	const standardCriteriaItems: models.CoverDataItem[] = [
		{
			label: '1. Qualification of NDT Personnel',
			value: props.coverSheetData?.ndtQualification,
		},
		{
			label: '2. Non Destructive Testing',
			value: props.coverSheetData?.nonDestructiveTesting,
		},
		{
			label: '3. Process Piping',
			value: props.coverSheetData?.processPiping,
		},
		{
			label: '4. Upstream Process Piping Welding and Inspection',
			value: props.coverSheetData?.upstreamProcessPiping,
		},
	];

	const photographicRecord = utils.getChunkedData(props.photographicRecord, 4);
	const pageSubtitle: string = 'Document Number: ' + documentNumber;

	return (
		<React.Fragment>
			<PDFInfoSheet
				companyUrl={props.projectDetails.organisation.organisationLogo}
				documentNo={props.projectDetails.documentNumber}
				clientName={props.projectDetails.organisationName}
				facility={props.projectDetails.facilityName}
				reportTitle={props.projectDetails.projectTitle}
			/>

			<PDFPage
				pageDetails={utils.getPageDetails('Cover Sheet', pageSubtitle)}
				projectDetails={props.projectDetails}
			>
				<CoverSheet
					overviewData={overviewData}
					referenceDocuments={referenceDocuments}
					standardCriteriaItems={standardCriteriaItems}
					note="Refer to work instruction for inspection requirement"
				/>
			</PDFPage>

			<PDFPage
				pageDetails={utils.getPageDetails('Summary Sheet', 'Summary Report')}
				projectDetails={props.projectDetails}
			>
				<RTWSummary {...props.summarySheetData} />
			</PDFPage>

			<PDFPage
				pageDetails={utils.getPageDetails('P & ID Sheet')}
				projectDetails={props.projectDetails}
			>
				<PDFPhotoPage photos={utils.getPIDImages(pidImages)} />
			</PDFPage>

			<PDFPage
				pageDetails={utils.getPageDetails('Isometric Drawing Sheet')}
				projectDetails={props.projectDetails}
			>
				<PDFPhotoPage photos={props.isoData} />
			</PDFPage>

			<PDFPage
				pageDetails={utils.getPageDetails('Radiography Data Page')}
				projectDetails={props.projectDetails}
			>
				<RTWDigitalRadiography
					{...props.radiographyData}
					summary={props.radiographySummary}
				/>
			</PDFPage>

			<PDFPage
				pageDetails={utils.getPageDetails('Liquid Penetrant Testing')}
				projectDetails={props.projectDetails}
			>
				<DyePenetrantInspection
					{...props.lptData}
					projectDetails={props.projectDetails}
				/>
			</PDFPage>

			<ETAnomalyPDF data={props.anomalyData} project={props.projectDetails} />

			{photographicRecord.map((photos, index: number) => (
				<PDFPage
					key={photos[index].src}
					enableTitlePage={index === 0}
					pageDetails={utils.getPageDetails('Photographic Record')}
					projectDetails={props.projectDetails}
				>
					<PDFPhotoPage photos={photos} />
				</PDFPage>
			))}
		</React.Fragment>
	);
};

const overviewData: models.CoverDataOverview[] = [
	{ report: '1. Cover Sheet', required: 'Yes', page: '3' },
	{ report: '2. Summary Sheet', required: 'Yes', page: '4' },
	{
		report: '3. P & ID Sheet',
		required: 'Yes',
		page: '5',
	},
	{
		report: '4. Isometric Drawing Sheet',
		required: 'Yes',
		page: '6',
	},
	{
		report: '5. Radiography Result',
		required: 'Yes',
		page: '7',
	},
	{
		report: '6. Magnetic Particle Inspection Report',
		required: 'Yes',
		page: '8',
	},
	{
		report: '7. Shear wave Inspection report (Weld thick =or > 10mm)',
		required: 'Yes',
		page: '8',
	},
	{
		report: '8. Anomaly Report',
		required: 'Yes',
		page: '9',
	},
	{
		report: '9. Photographic Record',
		required: 'Yes',
		page: '10',
	},
];

export default withPDF(RTWReport);
